import { backendApi } from "./backendApi.rtkq";

export interface App {
  appId: string;
  appIdentifier: string;
  name: string;
  description: string;
  category?: string;
  isActive: boolean;
  version?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CreateAppParams {
  name: string;
  appIdentifier: string;
  description: string;
  category?: string;
  isActive: boolean;
  version?: string;
}

export interface UpdateAppParams {
  appId: string;
  updates: Partial<Omit<App, "appId" | "createdAt">>;
}

export interface DeleteAppParams {
  appId: string;
}

export interface GetAppByIdParams {
  appId: string;
}

export interface GetAppsByCategoryParams {
  category: string;
}

export const appsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all apps
    getApps: builder.query<App[], void>({
      query: () => "/app-definitions",
      transformResponse: (response: any) => {
        const apps = response.apps || response || [];
        return apps.map((app: any) => ({
          ...app,
          appId: app.appId || app.id,
          createdAt: app.createdAt ? new Date(app.createdAt) : undefined,
          updatedAt: app.updatedAt ? new Date(app.updatedAt) : undefined,
        }));
      },
      providesTags: ["Apps"],
    }),

    // Get app by ID
    getAppById: builder.query<App | null, GetAppByIdParams>({
      query: ({ appId }) => `/app-definitions/${appId}`,
      transformResponse: (response: any) => {
        if (!response) return null;
        return {
          ...response,
          appId: response.appId || response.id,
          createdAt: response.createdAt ? new Date(response.createdAt) : undefined,
          updatedAt: response.updatedAt ? new Date(response.updatedAt) : undefined,
        };
      },
      providesTags: (result, _error, { appId }) =>
        result ? [{ type: "Apps", id: appId }] : [],
    }),

    // Create new app
    createApp: builder.mutation<{ appId: string; app: App }, CreateAppParams>({
      query: (appData) => ({
        url: "/app-definitions",
        method: "POST",
        body: appData,
      }),
      transformResponse: (response: any) => ({
        appId: response.appId || response.id,
        app: {
          ...response,
          appId: response.appId || response.id,
          createdAt: response.createdAt ? new Date(response.createdAt) : undefined,
          updatedAt: response.updatedAt ? new Date(response.updatedAt) : undefined,
        },
      }),
      invalidatesTags: ["Apps"],
    }),

    // Update existing app
    updateApp: builder.mutation<{ success: boolean }, UpdateAppParams>({
      query: ({ appId, updates }) => ({
        url: `/app-definitions/${appId}`,
        method: "PUT",
        body: updates,
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { appId }) =>
        result ? [{ type: "Apps", id: appId }, "Apps"] : [],
    }),

    // Delete app
    deleteApp: builder.mutation<{ success: boolean }, DeleteAppParams>({
      query: ({ appId }) => ({
        url: `/app-definitions/${appId}`,
        method: "DELETE",
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: ["Apps"],
    }),

    // Get apps by category — fetches all and filters client-side
    getAppsByCategory: builder.query<App[], GetAppsByCategoryParams>({
      query: () => "/app-definitions",
      transformResponse: (response: any, _meta, { category }: GetAppsByCategoryParams) => {
        const apps = response.apps || response || [];
        return apps
          .filter((app: any) => app.category === category)
          .map((app: any) => ({
            ...app,
            appId: app.appId || app.id,
            createdAt: app.createdAt ? new Date(app.createdAt) : undefined,
            updatedAt: app.updatedAt ? new Date(app.updatedAt) : undefined,
          }))
          .sort((a: App, b: App) => a.name.localeCompare(b.name));
      },
      providesTags: (result, _error, { category }) =>
        result ? [{ type: "Apps", id: `category-${category}` }] : [],
    }),
  }),
});

export const {
  useGetAppsQuery,
  useGetAppByIdQuery,
  useCreateAppMutation,
  useUpdateAppMutation,
  useDeleteAppMutation,
  useGetAppsByCategoryQuery,
} = appsApi;
